import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ArrowRightLine from '@/assets/icons/arrow_right_line.svg?component';
import { Button } from '@/components/atoms/Buttons';
import { Card } from '@/components/atoms/Card';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import PercentageBars from '@/components/organisms/PercentageBars';
import type { HealthOverviewParameter } from '@/domain/health';
import { COPY_EXCLUDE_ATTR } from '@/utils/elementCapture';

import {
    mapParameterToLaksvelScoreBars,
    mapParameterToTrendDatasets,
} from '../../healthOverviewChartMappers';
import HealthDevelopmentLineChart from '../HealthDevelopmentLineChart/HealthDevelopmentLineChart';

import styles from './LaksvelCompactSummaryCard.module.scss';

interface LaksvelCompactSummaryCardProps {
    parameter: HealthOverviewParameter;
    expanded: boolean;
    isLoading: boolean;
    fishCountLabel: string;
    onNavigate: (() => void) | null;
}

export default function LaksvelCompactSummaryCard(props: LaksvelCompactSummaryCardProps) {
    const { parameter, expanded, isLoading, fishCountLabel, onNavigate } = props;
    const { t } = useTranslation('healthModule');
    const { t: tGlobal } = useTranslation();

    const scoreBars = useMemo(() => mapParameterToLaksvelScoreBars(parameter, t), [parameter, t]);

    const trendDatasets = useMemo(() => mapParameterToTrendDatasets(parameter, t), [parameter, t]);

    return (
        <div className={styles.body} data-expanded={expanded}>
            <div className={styles.chartsRow}>
                <Card radius="sm" fullWidth={false} className={styles.chartPanel}>
                    <PercentageBars
                        title={t('dashboard.laksvelScoreTitle')}
                        items={scoreBars}
                        isLoading={isLoading}
                        decimals={0}
                    />
                </Card>
                <Card radius="sm" fullWidth={false} className={styles.chartPanel}>
                    <HealthDevelopmentLineChart
                        className={styles.developmentGraph}
                        datasets={trendDatasets}
                        parameterTrend={parameter.trend}
                        isLoading={isLoading}
                        chartHeight={180}
                    />
                </Card>
            </div>
            <p className={styles.fishCount}>
                {t('dashboard.measuredFishCount', {
                    count: fishCountLabel,
                } as Record<string, string>)}
            </p>
            {onNavigate && expanded ? (
                <div className={styles.footer} {...{ [COPY_EXCLUDE_ATTR]: '' }}>
                    <Button
                        variant="secondary"
                        trackingId="health_laksvel_view"
                        onClick={onNavigate}
                        iconEnd={
                            <SvgIcon
                                color="currentColor"
                                icon={ArrowRightLine}
                                size="custom"
                                customSize={20}
                            />
                        }>
                        {tGlobal('common.actions.view')}
                    </Button>
                </div>
            ) : null}
        </div>
    );
}
